import type { ReactNode } from "react";
import type { SortingFn } from "@tanstack/react-table";
import {
  Badge,
  Button,
  cn,
  SortableHeader,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "web-design-system";
import { ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { convertToCurrency, formatAsPercentage } from "creso-core/utils";
import type { SchemeFolioHolding } from "creso-core/types";
import { AmcLogo } from "../../logos";
import { ProductTypeBadge } from "../../badges";
import { NAV, Units } from "../../primitives";
import type { EditableColumnDef } from "../types";

/** Numeric sort for percentage columns - handles negative values correctly vs alphanumeric */
export const numericPercentageSort: SortingFn<SchemeFolioHolding> = (
  rowA,
  rowB,
  columnId
) => {
  const a = parseFloat(String(rowA.getValue(columnId) ?? "")) || 0;
  const b = parseFloat(String(rowB.getValue(columnId) ?? "")) || 0;
  return a - b;
};

export type HoldingsColumnOptions = {
  /** Wraps the scheme name of an internal holding, e.g. in a fund-detail link. */
  renderSchemeLink?: (args: {
    fundId: string;
    children: ReactNode;
  }) => ReactNode;
  /** Row action menu. Omit it and no action column is produced. */
  renderActions?: (holding: SchemeFolioHolding) => ReactNode;
};

export function createHoldingsColumns({
  renderSchemeLink,
  renderActions,
}: HoldingsColumnOptions = {}): EditableColumnDef<SchemeFolioHolding>[] {
  const columns: EditableColumnDef<SchemeFolioHolding>[] = [
    {
      accessorKey: "scheme_name",
      id: "scheme_name",
      label: "Scheme Name",
      configurable: false,
      header: ({ column }) => (
        <SortableHeader className="whitespace-nowrap" column={column}>
          Scheme name
        </SortableHeader>
      ),
      cell: ({ row }) =>
        row.depth === 0 ? (
          <div className="flex flex-row gap-2 items-center">
            <AmcLogo amc_code={row.original.amc_morningstar_code} />
            <div>
              {row.original.internal_flag && renderSchemeLink ? (
                renderSchemeLink({
                  fundId: row.original.fund_id,
                  children: (
                    <span className="text-sm">{row.original.scheme_name}</span>
                  ),
                })
              ) : (
                <span className="text-sm">{row.original.scheme_name}</span>
              )}
              <ProductTypeBadge
                className="ml-2"
                type={row.original.product_type}
              />
            </div>
          </div>
        ) : null,
      minSize: 280,
    },
    {
      id: "source",
      accessorKey: "internal_flag",
      header: "Source",
      label: "Source",
      configurable: true,
      cell: ({ row }) => (
        <span className="text-sm">
          <Badge
            variant="custom"
            className={cn(
              row.original.internal_flag
                ? "bg-tag-slate text-tag-slate-foreground"
                : "bg-tag-yellow text-tag-yellow-foreground"
            )}
          >
            {row.original.internal_flag ? "Internal" : "External"}
          </Badge>
        </span>
      ),
    },
    {
      accessorKey: "folio_number",
      id: "folio_number",
      label: "Folio Number",
      header: ({ column }) => (
        <SortableHeader className="whitespace-nowrap" column={column}>
          Folio no.
        </SortableHeader>
      ),
      cell: ({ row }) => {
        if (row.original.folio_data && row.original.folio_data.length === 1) {
          return (
            <span className="text-sm">
              {row.original.folio_data[0]?.folio_number}
            </span>
          );
        }

        return row.getCanExpand() ? (
          <Button
            variant="link"
            className="hover:no-underline gap-1 p-0"
            onClick={row.getToggleExpandedHandler()}
          >
            {row.original.folio_data?.length} folios
            <ChevronRight className={cn(row.getIsExpanded() && "rotate-90")} />
          </Button>
        ) : (
          <div className="flex flex-row gap-2 items-center">
            <span className="text-sm">{row.original.folio_number}</span>
          </div>
        );
      },
      minSize: 120,
    },
    {
      accessorKey: "invested_value",
      id: "invested_value",
      label: "Invested",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          Invested
        </SortableHeader>
      ),
      sortingFn: "alphanumeric",
      cell: (info) => (
        <span className="text-sm">
          {convertToCurrency(
            Math.round(Number(info.row.original.invested_value)),
            0
          )}
        </span>
      ),
      meta: { className: "text-right" },
    },
    {
      accessorKey: "current_value",
      id: "current_value",
      label: "Current",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          Current
        </SortableHeader>
      ),
      sortingFn: numericPercentageSort,
      cell: (info) => (
        <span className="text-sm">
          {convertToCurrency(
            Math.round(Number(info.row.original.current_value)),
            0
          )}
        </span>
      ),
      meta: { className: "text-right" },
    },
    {
      accessorKey: "absolute_return_pct",
      id: "absolute_return_pct",
      label: "Returns",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          Returns
        </SortableHeader>
      ),
      sortingFn: numericPercentageSort,
      cell: (info) => {
        const value = info.row.original.gain;
        const absoluteReturn = info.row.original.absolute_return_pct;
        const isPositive = Number(absoluteReturn) > 0;
        return (
          <div className="flex flex-col gap-1 text-sm">
            <span>{convertToCurrency(value ?? 0, 0)}</span>{" "}
            <div
              className={cn(
                "text-xs",
                Number(absoluteReturn) !== 0 && isPositive
                  ? "text-constructive"
                  : "text-destructive",
                Number(absoluteReturn) === 0 && "text-muted-foreground"
              )}
            >
              {formatAsPercentage(absoluteReturn ?? "", "N/A", 1)}
            </div>
          </div>
        );
      },
      meta: { className: "text-right" },
    },
    {
      accessorKey: "previous_day_return_pct",
      id: "previous_day_return_pct",
      label: "1D Returns",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader
          className="justify-end whitespace-nowrap"
          column={column}
        >
          1D Returns
        </SortableHeader>
      ),
      sortingFn: numericPercentageSort,
      cell: (info) => {
        const value = info.row.original.previous_day_change;
        const previousDayPct = info.row.original.previous_day_return_pct;
        const isPositive = Number(previousDayPct) > 0;
        return (
          <div className="flex flex-col gap-1 text-sm">
            <span>{convertToCurrency(value ?? 0, 0)}</span>{" "}
            <div
              className={cn(
                "text-xs",
                Number(previousDayPct) !== 0 && isPositive
                  ? "text-constructive"
                  : "text-destructive",
                Number(previousDayPct) === 0 && "text-muted-foreground"
              )}
            >
              {formatAsPercentage(previousDayPct ?? "", "N/A", 1)}
            </div>
          </div>
        );
      },
      meta: { className: "text-right" },
    },
    {
      accessorKey: "xirr",
      id: "xirr",
      label: "XIRR",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          XIRR
        </SortableHeader>
      ),
      sortingFn: numericPercentageSort,
      cell: (info) => {
        const { lifetime_xirr, xirr } = info.row.original;
        const xirrValue =
          lifetime_xirr && !Number.isNaN(lifetime_xirr) ? lifetime_xirr : xirr;
        const isPositive = lifetime_xirr
          ? Number(lifetime_xirr) > 0
          : Number(xirr) > 0;

        return (
          <div
            className={cn(
              "text-sm",
              Number(xirrValue) !== 0 &&
                (isPositive ? "text-constructive" : "text-destructive")
            )}
          >
            <span>{formatAsPercentage(xirrValue ?? "", "N/A", 1)}</span>
          </div>
        );
      },
      meta: { className: "text-right" },
    },
    {
      accessorKey: "units",
      id: "units",
      label: "Units",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          Units
        </SortableHeader>
      ),
      sortingFn: "alphanumeric",
      cell: (info) => <Units stripSuffix>{info.row.original.units}</Units>,
      meta: { className: "text-right" },
    },
    {
      accessorKey: "stcg",
      id: "stcg",
      label: "STCG",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          STCG
        </SortableHeader>
      ),
      sortingFn: numericPercentageSort,
      cell: (info) => {
        const { stcg, stcg_units } = info.row.original;
        const content = (
          <span className="text-sm">{convertToCurrency(stcg ?? 0, 0)}</span>
        );
        if (stcg_units && !Number.isNaN(Number(stcg_units))) {
          return (
            <Tooltip>
              <TooltipTrigger>{content}</TooltipTrigger>
              <TooltipContent>
                {Number(stcg_units).toFixed(1)} units
              </TooltipContent>
            </Tooltip>
          );
        }
        return content;
      },
      meta: { className: "text-right" },
    },
    {
      accessorKey: "ltcg",
      id: "ltcg",
      label: "LTCG",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          LTCG
        </SortableHeader>
      ),
      sortingFn: numericPercentageSort,
      cell: (info) => {
        const { ltcg, ltcg_units } = info.row.original;
        const content = (
          <span className="text-sm">{convertToCurrency(ltcg ?? 0, 0)}</span>
        );
        if (ltcg_units && !Number.isNaN(Number(ltcg_units))) {
          return (
            <Tooltip>
              <TooltipTrigger>{content}</TooltipTrigger>
              <TooltipContent>
                {Number(ltcg_units).toFixed(1)} units
              </TooltipContent>
            </Tooltip>
          );
        }
        return content;
      },
      meta: { className: "text-right" },
    },
    {
      accessorKey: "nav",
      id: "nav",
      label: "NAV",
      configurable: true,
      header: ({ column }) => (
        <SortableHeader className="justify-end" column={column}>
          NAV
        </SortableHeader>
      ),
      cell: ({ row }) => {
        const cell = <NAV className="text-sm" value={row.original.nav ?? 0} />;
        if (!row.original.nav_date) return cell;
        return (
          <Tooltip>
            <TooltipTrigger>{cell}</TooltipTrigger>
            <TooltipContent>
              {format(row.original.nav_date, "dd MMM yyyy")}
            </TooltipContent>
          </Tooltip>
        );
      },
      meta: { className: "text-right" },
    },
    {
      accessorKey: "is_demat",
      id: "isDemat",
      label: "Mode",
      configurable: true,
      header: () => (
        <div className="text-sm font-semibold flex justify-center">Mode</div>
      ),
      cell: (info) => {
        if (
          info.row.depth === 0 &&
          info.row.original.folio_data &&
          info.row.original.folio_data.length > 1
        ) {
          return null;
        }
        let { is_demat } = info.row.original;
        if (typeof is_demat === "undefined" || is_demat === null) {
          is_demat = info.row.original.folio_data?.[0]?.is_demat ?? false;
        }
        return (
          <Badge
            variant="custom"
            className={cn(
              is_demat
                ? "bg-tag-red text-tag-red-foreground"
                : "bg-tag-lime text-tag-lime-foreground"
            )}
          >
            {is_demat ? "Demat" : "Physical"}
          </Badge>
        );
      },
      meta: { className: "text-center" },
    },
  ];

  if (renderActions) {
    columns.push({
      accessorKey: "scheme_isin",
      header: "",
      id: "action",
      size: 50,
      minSize: 50,
      cell: ({ row }) => renderActions(row.original),
      meta: { className: "text-center" },
      configurable: false,
      label: "Action",
    });
  }

  return columns;
}
